import { useEffect, useRef, useState } from "react";
import { IS_LITE } from "./perf";

export type Position = {
  lat: number;
  lon: number;
  /** speed in m/s, or null if unavailable */
  speedMs: number | null;
  /** heading 0..360°, or null if unavailable */
  heading: number | null;
  /** accuracy in m */
  accuracy: number;
  ts: number;
  /** true when this position is synthetic / simulated (no real fix) */
  simulated?: boolean;
};

export type GeoOptions = {
  simulate?: boolean;
  /**
   * Optional GeoJSON LineString to drive the simulator along (each
   * coordinate is `[lon, lat]`, matching OSRM/GeoJSON convention). When
   * absent, the simulator runs a default loop around central Copenhagen.
   */
  simulateAlong?: { coordinates: [number, number][] } | null;
};

/**
 * Live GPS diagnostics — a single mutable module-level object so the
 * debug panel can read it without forcing re-renders on the hot path.
 * onFix writes the raw + derived fix data; the loop writes the live
 * dead-reckoning state. The panel polls this on a timer.
 */
export type GeoStats = {
  source: "gps" | "sim" | "none";
  fixCount: number;
  /** wall-clock (Date.now) of the last real fix */
  lastFixTs: number;
  /** ms between the two most recent fixes */
  fixIntervalMs: number;
  /** smoothed average fix interval */
  avgFixIntervalMs: number;
  /** device-reported values (null when the GPS omits them) */
  rawSpeedMs: number | null;
  rawHeading: number | null;
  accuracyM: number | null;
  /** values derived from successive-fix differencing */
  derivedSpeedMs: number | null;
  derivedHeading: number | null;
  /** what the dead-reckoning actually used */
  usedSpeedMs: number | null;
  usedHeading: number | null;
  /** live loop state */
  deadReckon: boolean;
  predictAheadM: number;
  lat: number | null;
  lon: number | null;
};

export const geoStats: GeoStats = {
  source: "none",
  fixCount: 0,
  lastFixTs: 0,
  fixIntervalMs: 0,
  avgFixIntervalMs: 0,
  rawSpeedMs: null,
  rawHeading: null,
  accuracyM: null,
  derivedSpeedMs: null,
  derivedHeading: null,
  usedSpeedMs: null,
  usedHeading: null,
  deadReckon: false,
  predictAheadM: 0,
  lat: null,
  lon: null,
};

const DEFAULT_ANCHORS: [number, number][] = [
  // [lat, lon] tuples — same as the previous loop
  [55.6761, 12.5683],
  [55.6800, 12.5750],
  [55.6845, 12.5810],
  [55.6855, 12.5950],
  [55.6820, 12.6030],
  [55.6760, 12.6010],
  [55.6710, 12.5910],
  [55.6700, 12.5790],
  [55.6730, 12.5710],
];

function haversine(aLat: number, aLon: number, bLat: number, bLon: number) {
  const R = 6_371_000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(bLat - aLat);
  const dLon = toRad(bLon - aLon);
  const x =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(aLat)) * Math.cos(toRad(bLat)) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
}

/** Shortest signed arc target−current, in (-180, 180]. */
function shortestArc(target: number, current: number): number {
  let d = target - current;
  d = ((d % 360) + 540) % 360 - 180;
  return d;
}

/**
 * Project a lat/lon forward by `distM` metres along a compass heading.
 * Flat-earth approximation — fine for the few-metres-per-frame deltas
 * we dead-reckon with.
 */
function projectForward(lat: number, lon: number, headingDeg: number, distM: number): [number, number] {
  const mPerDegLat = 111_320;
  const mPerDegLon = 111_320 * Math.cos((lat * Math.PI) / 180) || 1;
  const rad = (headingDeg * Math.PI) / 180;
  const dLat = (distM * Math.cos(rad)) / mPerDegLat;
  const dLon = (distM * Math.sin(rad)) / mPerDegLon;
  return [lat + dLat, lon + dLon];
}

/** Initial compass bearing A→B, degrees clockwise from north (0..360). */
function bearingBetween(aLat: number, aLon: number, bLat: number, bLon: number): number {
  const mLat = 111_320;
  const mLon = 111_320 * Math.cos((aLat * Math.PI) / 180);
  const east = (bLon - aLon) * mLon;
  const north = (bLat - aLat) * mLat;
  let h = Math.atan2(east, north) * (180 / Math.PI);
  if (h < 0) h += 360;
  return h;
}

// Once a real fix arrives we never fall back to the simulator (the
// simulator is only for preview / no-GPS). If GPS goes quiet we keep
// dead-reckoning forward, but only up to this many seconds so a dropped
// signal doesn't send the marker flying off the map. Phone/head-unit
// GPS often delivers a fix only every 1–4 s, so this needs to comfortably
// cover the gap between fixes.
const MAX_PREDICT_S = 4;
// Exponential smoothing time-constants (seconds). Converted to a
// per-frame factor via 1 − e^(−dt/τ) so the feel is frame-rate
// independent (matters because lite mode ticks at ~12 Hz, full at 60).
const POS_TAU = 0.18;
const HEAD_TAU = 0.28;

/**
 * useGeolocation
 *
 * Real device fix (Capacitor native plugin, or the browser API) is
 * authoritative the moment it arrives — the simulator only runs until
 * then (preview / no-GPS). Because phones deliver fixes slowly (~1 Hz)
 * and Android frequently omits `speed`/`heading`, we don't render fixes
 * directly: an animation loop dead-reckons the displayed position
 * forward from the latest fix (using its velocity) and eases toward
 * that prediction, so the map flows smoothly between sparse updates.
 *
 * If `simulateAlong` is provided (an OSRM/GeoJSON LineString) the
 * simulator drives along that polyline; otherwise it runs the default
 * Copenhagen loop. Either way, a real fix immediately takes over.
 */
export function useGeolocation(options: GeoOptions = {}) {
  const { simulate = true, simulateAlong = null } = options;
  const [position, setPosition] = useState<Position | null>(null);
  const realFixRef = useRef<Position | null>(null);
  // True once we've received at least one real fix. From then on the
  // simulator is permanently disabled — even if GPS drops out, we hold
  // / coast rather than teleporting back to the simulated track.
  const everRealRef = useRef(false);
  const watchIdRef = useRef<number | null>(null);
  // Previous raw fix, kept so we can derive speed + heading from the
  // displacement between consecutive fixes (car head-unit GPS very often
  // reports speed/heading as null — without a derived velocity the
  // dead-reckoning never engages and the marker freezes between fixes).
  const prevFixRef = useRef<{ lat: number; lon: number; ts: number } | null>(null);

  useEffect(() => {
    const onFix = (np: Position) => {
      const prev = prevFixRef.current;
      let speedMs = np.speedMs;
      let heading = np.heading;
      let derivedSpeed: number | null = null;
      let derivedHeading: number | null = null;
      if (prev) {
        const dt = (np.ts - prev.ts) / 1000;
        if (dt > 0.05) {
          const dist = haversine(prev.lat, prev.lon, np.lat, np.lon);
          if (dist < 3) {
            // Moved less than GPS noise — treat as stationary. Don't let
            // the device's (possibly stale) speed keep us coasting, and
            // don't manufacture speed from jitter.
            derivedSpeed = 0;
            if (speedMs == null || speedMs < 0.5) speedMs = 0;
          } else {
            // Derive speed + heading from how far we actually moved.
            // Head-unit GPS commonly reports speed/heading as null OR a
            // flat 0, so we override anything that isn't a usable value.
            derivedSpeed = dist / dt;
            derivedHeading = bearingBetween(prev.lat, prev.lon, np.lat, np.lon);
            if (speedMs == null || speedMs < 0.5) speedMs = derivedSpeed;
            if (heading == null) heading = derivedHeading;
          }
        }
      }
      realFixRef.current = { ...np, speedMs, heading };
      everRealRef.current = true;
      prevFixRef.current = { lat: np.lat, lon: np.lon, ts: np.ts };

      // ── diagnostics ──
      const recv = Date.now();
      geoStats.source = "gps";
      geoStats.fixCount += 1;
      if (geoStats.lastFixTs) {
        const interval = recv - geoStats.lastFixTs;
        geoStats.fixIntervalMs = interval;
        geoStats.avgFixIntervalMs = geoStats.avgFixIntervalMs
          ? geoStats.avgFixIntervalMs * 0.7 + interval * 0.3
          : interval;
      }
      geoStats.lastFixTs = recv;
      geoStats.rawSpeedMs = np.speedMs;
      geoStats.rawHeading = np.heading;
      geoStats.accuracyM = np.accuracy;
      geoStats.derivedSpeedMs = derivedSpeed;
      geoStats.derivedHeading = derivedHeading;
      geoStats.usedSpeedMs = speedMs;
      geoStats.usedHeading = heading;
      geoStats.lat = np.lat;
      geoStats.lon = np.lon;
    };
    // The watch is (re)created by subscribe(). It must be re-runnable:
    // after a long WebView suspend (ignition off overnight) the Android
    // location watch silently dies and never resumes on its own, so the
    // app would sit in the simulator forever. We re-subscribe on resume
    // (visibilitychange) and via a staleness watchdog.
    let watchCleanup: (() => void) | null = null;
    let disposed = false;
    let lastSubscribeAt = 0;

    const subscribe = async () => {
      lastSubscribeAt = Date.now();
      // tear down any previous watch first
      if (watchCleanup) { try { watchCleanup(); } catch {} watchCleanup = null; }
      // Don't derive speed/heading across the re-subscribe gap.
      prevFixRef.current = null;

      try {
        const isNative = !!(window as unknown as { Capacitor?: { isNativePlatform: () => boolean } })
          .Capacitor?.isNativePlatform?.();
        if (isNative) {
          const { Geolocation } = await import("@capacitor/geolocation");
          const perm = await Geolocation.requestPermissions();
          if (perm.location === "denied" && perm.coarseLocation === "denied") return;
          // `interval` + `minimumUpdateInterval` are @capacitor/geolocation
          // (v8) Android extensions NOT in the W3C PositionOptions type, so
          // we widen the type. When `interval` is omitted the plugin
          // defaults it to `timeout` — ask for 1 Hz explicitly.
          const id = await Geolocation.watchPosition(
            {
              enableHighAccuracy: true,
              timeout: 10_000,
              maximumAge: 0,
              interval: 1000,
              minimumUpdateInterval: 1000,
            } as unknown as Parameters<typeof Geolocation.watchPosition>[0],
            (p, err) => {
              if (err || !p) return;
              onFix({
                lat: p.coords.latitude,
                lon: p.coords.longitude,
                speedMs: Number.isFinite(p.coords.speed as number) ? (p.coords.speed as number) : null,
                heading: Number.isFinite(p.coords.heading as number) ? (p.coords.heading as number) : null,
                accuracy: p.coords.accuracy ?? 20,
                ts: p.timestamp,
              });
            },
          );
          if (disposed) { Geolocation.clearWatch({ id }).catch(() => {}); return; }
          watchCleanup = () => { Geolocation.clearWatch({ id }).catch(() => {}); };
          return;
        }
      } catch {
        // fall through to browser API
      }
      if (!("geolocation" in navigator)) return;
      try {
        const id = navigator.geolocation.watchPosition(
          (p) => {
            onFix({
              lat: p.coords.latitude,
              lon: p.coords.longitude,
              speedMs: Number.isFinite(p.coords.speed as number) ? (p.coords.speed as number) : null,
              heading: Number.isFinite(p.coords.heading as number) ? (p.coords.heading as number) : null,
              accuracy: p.coords.accuracy,
              ts: p.timestamp,
            });
          },
          () => { /* permission denied or no fix — sim takes over */ },
          { enableHighAccuracy: true, maximumAge: 0, timeout: 15000 },
        );
        watchIdRef.current = id;
        if (disposed) { navigator.geolocation.clearWatch(id); return; }
        watchCleanup = () => navigator.geolocation.clearWatch(id);
      } catch {}
    };

    subscribe();

    // Re-subscribe when the WebView becomes visible again (Android wakes
    // the activity after sleep). The pre-sleep watch is usually dead.
    const onVisible = () => {
      if (document.visibilityState === "visible") subscribe();
    };
    document.addEventListener("visibilitychange", onVisible);

    // Watchdog: revive a dead watch. If no fix has arrived for a while
    // AND we haven't just re-subscribed, try again. The cooldown gives a
    // cold GPS time to acquire (network-assisted is usually < 10 s) so we
    // don't restart acquisition mid-flight.
    const watchdog = window.setInterval(() => {
      const sinceFix = geoStats.lastFixTs ? Date.now() - geoStats.lastFixTs : Infinity;
      const sinceSub = Date.now() - lastSubscribeAt;
      if (sinceFix > 25_000 && sinceSub > 30_000) subscribe();
    }, 10_000);

    return () => {
      disposed = true;
      document.removeEventListener("visibilitychange", onVisible);
      clearInterval(watchdog);
      if (watchCleanup) { try { watchCleanup(); } catch {} }
    };
  }, []);

  // Resolve the route to drive (when given). [lon,lat] → [lat,lon] for our use.
  const routePoints: [number, number][] | null = simulateAlong?.coordinates?.length
    ? simulateAlong.coordinates.map(([lon, lat]) => [lat, lon])
    : null;
  // Bump a key when the route reference truly changes so the effect resets.
  const routeSignature = routePoints
    ? `${routePoints.length}:${routePoints[0][0]},${routePoints[0][1]}:${routePoints.at(-1)![0]},${routePoints.at(-1)![1]}`
    : null;

  useEffect(() => {
    let raf = 0;
    let timerId: number | null = null;
    const lerp = (a: number, b: number, x: number) => a + (b - a) * x;
    let lastTs = performance.now();
    let simSpeed = routePoints ? 30 : 50;

    // Real-mode display state (interpolated). null until first real fix.
    let dispLat: number | null = null;
    let dispLon: number | null = null;
    let dispHeading = 0;
    let prevDispLat: number | null = null;
    let prevDispLon: number | null = null;

    // In lite mode, throttle to ~12 Hz via setTimeout instead of every
    // animation frame. Per-second JS load drops ~5×; smoothness is fine
    // because the easing factor adapts to dt.
    const schedule = IS_LITE
      ? (fn: () => void) => { timerId = window.setTimeout(fn, 85); }
      : (fn: () => void) => { raf = requestAnimationFrame(fn); };

    /* ---------- ROUTE-FOLLOW SIM SETUP ---------- */
    const routeMode = !!(routePoints && routePoints.length >= 2);
    const segLens: number[] = [];
    if (routeMode) {
      for (let i = 0; i < routePoints!.length - 1; i++) {
        segLens.push(haversine(routePoints![i][0], routePoints![i][1], routePoints![i + 1][0], routePoints![i + 1][1]));
      }
    }
    let segIdx = 0;
    let segT = 0;

    /* ---------- DEFAULT LOOP SIM SETUP ---------- */
    const anchors = DEFAULT_ANCHORS;
    let t = 0;
    let simLastLat = anchors[0][0];
    let simLastLon = anchors[0][1];
    const sampleAnchors = (tt: number) => {
      const n = anchors.length;
      const i0 = Math.floor(tt) % n;
      const i1 = (i0 + 1) % n;
      const i2 = (i0 + 2) % n;
      const f = tt - Math.floor(tt);
      const ease = f * f * (3 - 2 * f);
      const lat = lerp(lerp(anchors[i0][0], anchors[i1][0], ease), anchors[i1][0] + (anchors[i2][0] - anchors[i0][0]) * 0.04, 0);
      const lon = lerp(lerp(anchors[i0][1], anchors[i1][1], ease), anchors[i1][1] + (anchors[i2][1] - anchors[i0][1]) * 0.04, 0);
      return [lat, lon] as const;
    };

    const tick = () => {
      const perfNow = performance.now();
      const dt = Math.min(0.1, (perfNow - lastTs) / 1000);
      lastTs = perfNow;
      const now = Date.now();

      const real = realFixRef.current;

      /* ========== REAL MODE (we've had a fix — authoritative) ========== */
      if (everRealRef.current && real) {
        const ageS = Math.max(0, (now - real.ts) / 1000);
        const predictS = Math.min(ageS, MAX_PREDICT_S);

        // Dead-reckon the target forward from the fix using its velocity.
        // If speed/heading are missing (common on Android at low speed)
        // the target is just the fix itself — we still ease toward it,
        // which removes jitter and keeps motion continuous.
        let tLat = real.lat;
        let tLon = real.lon;
        const canPredict = real.speedMs != null && real.speedMs > 0.5 && real.heading != null;
        if (canPredict) {
          [tLat, tLon] = projectForward(real.lat, real.lon, real.heading!, real.speedMs! * predictS);
        }
        geoStats.deadReckon = canPredict;
        geoStats.predictAheadM = canPredict ? real.speedMs! * predictS : 0;

        // First fix: snap (avoid sliding across the map from origin).
        if (dispLat == null || dispLon == null) {
          dispLat = tLat;
          dispLon = tLon;
        }

        // Frame-rate-independent exponential ease toward the target.
        const kPos = 1 - Math.exp(-dt / POS_TAU);
        dispLat += (tLat - dispLat) * kPos;
        dispLon += (tLon - dispLon) * kPos;

        // Heading: prefer the GPS-reported value; otherwise derive it
        // from how the displayed point is moving (so the map still
        // rotates heading-up when Android omits bearing).
        let targetHeading = real.heading;
        if (targetHeading == null && prevDispLat != null && prevDispLon != null) {
          const dLatM = (dispLat - prevDispLat) * 111_320;
          const dLonM = (dispLon - prevDispLon) * 111_320 * Math.cos((dispLat * Math.PI) / 180);
          if (Math.hypot(dLatM, dLonM) > 0.4) {
            let h = Math.atan2(dLonM, dLatM) * (180 / Math.PI);
            if (h < 0) h += 360;
            targetHeading = h;
          }
        }
        let outHeading: number | null = null;
        if (targetHeading != null) {
          const kHead = 1 - Math.exp(-dt / HEAD_TAU);
          dispHeading = dispHeading + shortestArc(targetHeading, dispHeading) * kHead;
          dispHeading = ((dispHeading % 360) + 360) % 360;
          outHeading = dispHeading;
        }

        prevDispLat = dispLat;
        prevDispLon = dispLon;

        setPosition({
          lat: dispLat,
          lon: dispLon,
          speedMs: real.speedMs,
          heading: outHeading,
          accuracy: real.accuracy,
          ts: now,
        });
        schedule(tick);
        return;
      }

      /* ========== SIM MODE (never had a real fix) ========== */
      if (!simulate) { schedule(tick); return; }
      if (geoStats.source !== "sim") { geoStats.source = "sim"; geoStats.deadReckon = false; }

      if (routeMode) {
        const cycle = (perfNow / 1000 / 30) % 1;
        let target;
        if (cycle < 0.2) target = 30;
        else if (cycle < 0.45) target = 55;
        else if (cycle < 0.7) target = 80;
        else if (cycle < 0.85) target = 105;
        else target = 45;
        simSpeed += (target - simSpeed) * 0.06;

        let remaining = (simSpeed / 3.6) * dt;
        while (remaining > 0 && segIdx < routePoints!.length - 1) {
          const segLen = segLens[segIdx];
          const localRemaining = segLen * (1 - segT);
          if (remaining < localRemaining) {
            segT += remaining / segLen;
            remaining = 0;
          } else {
            remaining -= localRemaining;
            segIdx++;
            segT = 0;
          }
        }
        if (segIdx >= routePoints!.length - 1) { segIdx = 0; segT = 0; }

        const [aLat, aLon] = routePoints![segIdx];
        const [bLat, bLon] = routePoints![segIdx + 1] || routePoints![segIdx];
        const lat = lerp(aLat, bLat, segT);
        const lon = lerp(aLon, bLon, segT);
        let heading = Math.atan2(bLon - aLon, bLat - aLat) * (180 / Math.PI);
        if (heading < 0) heading += 360;

        setPosition({ lat, lon, speedMs: simSpeed / 3.6, heading, accuracy: 15, ts: now, simulated: true });
        schedule(tick);
        return;
      }

      // Default Copenhagen loop
      const cycle = (perfNow / 1000 / 24) % 1;
      if (cycle < 0.25) simSpeed = lerp(simSpeed, 28, 0.04);
      else if (cycle < 0.5) simSpeed = lerp(simSpeed, 62, 0.05);
      else if (cycle < 0.65) simSpeed = lerp(simSpeed, 88, 0.06);
      else if (cycle < 0.85) simSpeed = lerp(simSpeed, 116, 0.05);
      else simSpeed = lerp(simSpeed, 42, 0.05);

      t += ((simSpeed / 3.6) * dt / 600) * 0.5;
      const [lat, lon] = sampleAnchors(t);
      let heading = Math.atan2(lon - simLastLon, lat - simLastLat) * (180 / Math.PI);
      if (heading < 0) heading += 360;
      simLastLat = lat; simLastLon = lon;

      setPosition({ lat, lon, speedMs: simSpeed / 3.6, heading, accuracy: 15, ts: now, simulated: true });
      schedule(tick);
    };
    schedule(tick);
    return () => { cancelAnimationFrame(raf); if (timerId != null) clearTimeout(timerId); };
  }, [simulate, routeSignature]);

  return position;
}
